import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  inferKind,
  useProject,
  type IntakeOrganization,
} from "../api";
import { DrivePickerPanel } from "../components/DrivePickerPanel";
import { GmailSearchPanel } from "../components/GmailSearchPanel";
import { useIntakeSubmitContext } from "../state/useIntakeSubmitContext";

import styles from "./NewProjectRoute.module.css";

// LocalStorage key for the form draft. Saved on every keystroke,
// loaded on mount. Lets the operator partially fill the form, hard-
// reload (or close the tab and reopen), and pick up where they left
// off. We do NOT persist the picked files (browser File objects
// don't serialise to JSON cleanly + would balloon localStorage
// quota); the operator has to re-pick those after a reload.
const DRAFT_STORAGE_KEY = "inrange-agents.new-project.draft";

const loadDraft = (): IntakeFormState | null => {
  try {
    const raw = window.localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<IntakeFormState>;
    // Sanity-check the shape so a stale / corrupted draft from an
    // older schema doesn't poison the form.
    if (typeof parsed.project_id !== "string") return null;
    return {
      project_id: parsed.project_id,
      address: typeof parsed.address === "string" ? parsed.address : "",
      mpans_csv: typeof parsed.mpans_csv === "string" ? parsed.mpans_csv : "",
      ask: typeof parsed.ask === "string" ? parsed.ask : "",
      organizations: Array.isArray(parsed.organizations)
        ? parsed.organizations.filter(
            (o): o is IntakeOrganization =>
              typeof o === "object" &&
              o !== null &&
              typeof (o as IntakeOrganization).name === "string",
          )
        : [{ name: "", type: "" }],
    };
  } catch {
    return null;
  }
};

const saveDraft = (form: IntakeFormState): void => {
  try {
    window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(form));
  } catch {
    // Quota exceeded / disabled — silently drop. Form still works,
    // operator just loses the persistence-across-reload benefit.
  }
};

const clearDraft = (): void => {
  try {
    window.localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // ignore
  }
};

// Closed enum from the legacy app's ProjectInputForm. Same shape so
// the backend's organisation handling is consistent across UIs.
const ORG_TYPES = [
  "Landlord",
  "Tenant",
  "Owner-occupier",
  "Funder",
  "EPC",
  "Agent",
  "Broker",
  "Other",
] as const;

interface IntakeFormState {
  project_id: string;
  address: string;
  mpans_csv: string;
  ask: string;
  organizations: IntakeOrganization[];
}

const EMPTY_FORM: IntakeFormState = {
  project_id: "",
  address: "",
  mpans_csv: "",
  ask: "",
  organizations: [{ name: "", type: "" }],
};

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export const NewProjectRoute = () => {
  const [searchParams] = useSearchParams();
  const prefillProjectId = searchParams.get("project");
  const intake = useIntakeSubmitContext();

  // Initialise from localStorage draft (if any) on first render.
  // useState's initialiser fn runs once per mount, which is what we
  // want — re-mounts (after a hard reload) pick the draft up; normal
  // re-renders don't keep clobbering the form.
  const [form, setForm] = useState<IntakeFormState>(() => loadDraft() ?? EMPTY_FORM);
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Persist the form to localStorage on every change. Cheap (one
  // JSON.stringify per keystroke); the form is small.
  useEffect(() => {
    saveDraft(form);
  }, [form]);

  // Mirror the provider's stage into a route-local `progress` so the
  // existing UI bits (submit-button spinner, "Atlas working" status
  // line) keep working without touching the markup. The provider
  // owns the truth; this is a passive read.
  const progress = useMemo(() => {
    if (intake.state.stage === "uploading" || intake.state.stage === "atlas") {
      return { stage: intake.state.stage, label: intake.state.label };
    }
    return { stage: "idle" as const, label: "" };
  }, [intake.state.stage, intake.state.label]);

  // When ?project=<id> is present, load the project record and
  // pre-fill the form. Lets the operator "open" a queued project
  // from Operations (typically a bulk-imported row with no runs
  // yet) and submit it without re-typing.
  const { data: prefillProject } = useProject(prefillProjectId ?? undefined);
  const [hasPrefilled, setHasPrefilled] = useState(false);

  useEffect(() => {
    if (!prefillProjectId) return;
    if (hasPrefilled) return; // only run once per mount
    setHasPrefilled(true);
    // Pre-seed project_id immediately so an operator can submit
    // even if the /projects/get request is slow or the project
    // doesn't exist yet (manual id typed into the URL).
    setForm((prev) => ({ ...prev, project_id: prefillProjectId }));
  }, [prefillProjectId, hasPrefilled]);

  useEffect(() => {
    if (!prefillProject) return;
    // Hydrate fields the project record carries. We do not stomp
    // values the operator has already changed locally - we only
    // fill empty slots.
    setForm((prev) => {
      const orgs: IntakeOrganization[] = [];
      if (prefillProject.landlord)
        orgs.push({ name: prefillProject.landlord, type: "Landlord" });
      if (prefillProject.primary_epc)
        orgs.push({ name: prefillProject.primary_epc, type: "EPC" });
      return {
        project_id: prev.project_id || prefillProject.project_id,
        address: prev.address || prefillProject.address || "",
        mpans_csv: prev.mpans_csv || prefillProject.mpan || "",
        ask: prev.ask,
        organizations:
          prev.organizations[0]?.name || orgs.length === 0
            ? prev.organizations
            : orgs,
      };
    });
  }, [prefillProject]);

  const canSubmit = useMemo(
    () =>
      form.project_id.trim().length > 0 &&
      progress.stage !== "uploading" &&
      progress.stage !== "atlas",
    [form.project_id, progress.stage],
  );

  const updateField = useCallback(
    <K extends keyof IntakeFormState>(field: K, value: IntakeFormState[K]) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const updateOrg = (i: number, field: keyof IntakeOrganization, value: string) => {
    setForm((prev) => ({
      ...prev,
      organizations: prev.organizations.map((o, idx) =>
        idx === i ? { ...o, [field]: value } : o,
      ),
    }));
  };

  const addOrg = () =>
    setForm((prev) => ({
      ...prev,
      organizations: [...prev.organizations, { name: "", type: "" }],
    }));

  const removeOrg = (i: number) =>
    setForm((prev) => {
      const next = prev.organizations.filter((_, idx) => idx !== i);
      return {
        ...prev,
        organizations: next.length ? next : [{ name: "", type: "" }],
      };
    });

  const onPickFiles = (picked: FileList | null) => {
    if (!picked) return;
    setFiles((prev) => [...prev, ...Array.from(picked)]);
  };

  // Used by both the dropzone + the GmailSearchPanel. The panel
  // hands us picked threads as `File` objects (already text/plain
  // with a slug-of-subject name) so we just append to the same
  // state the dropzone writes to; the upload + inferKind pipeline
  // tags them as kind='email_thread'.
  const addFiles = useCallback((picked: File[]) => {
    if (picked.length === 0) return;
    setFiles((prev) => [...prev, ...picked]);
  }, []);

  // Drive panel can suggest a project_id from the picked folder
  // name. Only adopt when the form has no project_id yet (a fresh
  // New Project session); opening an existing project via
  // ?project=<id> must not silently rename the project.
  const onProjectIdSuggested = useCallback((suggested: string) => {
    setForm((prev) =>
      prev.project_id.trim() === "" ? { ...prev, project_id: suggested } : prev,
    );
  }, []);

  const removeFile = (i: number) => {
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    onPickFiles(e.dataTransfer.files);
  };

  const submit = useCallback(() => {
    if (!canSubmit) return;
    // Fire-and-forget. The IntakeSubmitProvider drives all progress
    // state + auto-navigates the AppLayout to /runs/:id when Atlas
    // returns.
    //
    // We DO NOT await — that's the whole point of the lift. If the
    // operator clicks Submit then immediately clicks Operations in
    // the sidebar, the route unmounts; the provider keeps running;
    // the auto-navigate fires when ready. The await would attach the
    // navigate to the route component, defeating the whole change.
    //
    // We DO chain `.then(clearDraft)` so the localStorage draft only
    // clears on a successful resolve. A transient S3 PUT failure or
    // an /intake validation error preserves the draft so the
    // operator's typed metadata + MPAN list survive a retry (the
    // file picker still has to be re-populated either way, browser
    // File objects don't survive reload).
    intake
      .submit({
        project_id: form.project_id.trim(),
        address: form.address.trim(),
        ask: form.ask.trim(),
        mpans_csv: form.mpans_csv,
        organizations: form.organizations,
        files,
      })
      .then(() => clearDraft())
      .catch(() => {
        // Provider has stored the error; pill surfaces it. Nothing
        // to do here, just suppress the unhandled-rejection.
      });
  }, [canSubmit, intake, form, files]);

  const errMsg = intake.state.stage === "error" && intake.state.error
    ? intake.state.error.message
    : null;

  // While an upload + intake is in flight (typically 30-120s) the
  // form fields and the dropzone are locked. The Save button alone
  // isn't enough - operators fiddle with the form during the wait
  // and edits stage no-ops, producing a confusing "did my change
  // count?" UX. Disabling at the fieldset level is the cheapest fix.
  const inflight =
    progress.stage === "uploading" || progress.stage === "atlas";

  return (
    <div className={styles.page}>
      {prefillProjectId && (
        <div className={styles.banner}>
          <div className={styles.bannerHead}>
            Opened from Operations
          </div>
          <div className={styles.bannerBody}>
            Editing <code>{prefillProjectId}</code>
            {prefillProject ? (
              <>
                {" — "}
                fields pre-filled from the stored project record. Add documents
                and submit to produce a new run.
              </>
            ) : (
              <> — loading project record…</>
            )}
            <Link to="/operations" className={styles.bannerLink}>
              Back to Operations
            </Link>
          </div>
        </div>
      )}
      <fieldset className={styles.fieldset} disabled={inflight}>
      <section className={styles.section}>
        <h2 className={styles.h2}>Project input</h2>
        <p className={styles.lede}>
          What's the project, where is it, and what do you want the agent to do?
        </p>

        <div className={styles.grid}>
          <Field label="Project ID" hint="e.g. CDS#AVONMOUTH#STAGE2">
            <input
              className={styles.input}
              value={form.project_id}
              onChange={(e) => updateField("project_id", e.target.value)}
              placeholder="CDS#AVONMOUTH#STAGE2"
            />
          </Field>

          <Field label="Address" hint="Free text; lat/lon if available">
            <input
              className={styles.input}
              value={form.address}
              onChange={(e) => updateField("address", e.target.value)}
              placeholder="The Range Avonmouth, Bristol BS35 4EL"
            />
          </Field>

          <Field label="MPANs" hint="Comma-separated 13- or 21-digit; optional">
            <input
              className={styles.input}
              value={form.mpans_csv}
              onChange={(e) => updateField("mpans_csv", e.target.value)}
              placeholder="2000056866598, 2000056868530"
            />
          </Field>

          <Field
            label="Ask"
            hint="What do you want the agent to do?"
            wide
          >
            <textarea
              className={styles.textarea}
              value={form.ask}
              onChange={(e) => updateField("ask", e.target.value)}
              placeholder="Review the Stage 2 grid offers. Tell me about open disputes, time-critical actions, and any cost commitments I need to authorise."
            />
          </Field>

          <Field
            label="Organisations"
            hint="Landlord, tenant, EPC, funder, etc. Add as many as apply."
            wide
          >
            <div className={styles.orgList}>
              {form.organizations.map((org, i) => (
                <div key={i} className={styles.orgRow}>
                  <input
                    className={styles.orgName}
                    value={org.name}
                    onChange={(e) => updateOrg(i, "name", e.target.value)}
                    placeholder="Organisation name"
                  />
                  <select
                    className={styles.orgType}
                    value={org.type ?? ""}
                    onChange={(e) => updateOrg(i, "type", e.target.value)}
                  >
                    <option value="">Type…</option>
                    {ORG_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => removeOrg(i)}
                    className={styles.orgRemove}
                    aria-label="Remove organisation"
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addOrg}
                className={styles.orgAdd}
              >
                + Add another organisation
              </button>
            </div>
          </Field>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Documents</h2>
        <p className={styles.lede}>
          Drop or pick the files Atlas should read. PDFs, Word, Excel, images
          (photos analysed via vision), CAD, .eml / .txt emails. The agent
          tags each file by inferred kind so the right specialist handles it.
        </p>

        <div
          className={`${styles.dropzone} ${dragOver ? styles.dropzoneActive : ""} ${inflight ? styles.dropzoneDisabled : ""}`}
          onDragOver={inflight ? undefined : onDragOver}
          onDragLeave={inflight ? undefined : onDragLeave}
          onDrop={inflight ? undefined : onDrop}
          onClick={inflight ? undefined : () => fileInputRef.current?.click()}
          role="button"
          tabIndex={inflight ? -1 : 0}
          aria-disabled={inflight}
        >
          <div className={styles.dropzoneIcon}>+</div>
          <div className={styles.dropzonePrimary}>
            Drop files here or click to pick
          </div>
          <div className={styles.dropzoneSecondary}>
            Files upload directly to S3 via presigned URLs; tagged by inferred
            kind for Atlas to route.
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className={styles.fileInput}
            onChange={(e) => onPickFiles(e.target.files)}
          />
        </div>

        {files.length > 0 && (
          <ul className={styles.fileList}>
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className={styles.fileRow}>
                <div className={styles.fileMain}>
                  <span className={styles.fileName}>{f.name}</span>
                  <span className={styles.fileMeta}>
                    {inferKind(f.name)} · {formatBytes(f.size)}
                  </span>
                </div>
                <button
                  type="button"
                  className={styles.fileRemove}
                  onClick={() => removeFile(i)}
                  aria-label={`Remove ${f.name}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <DrivePickerPanel
        onAddFiles={addFiles}
        disabled={inflight}
        onProjectIdSuggested={onProjectIdSuggested}
      />

      <GmailSearchPanel onAddFiles={addFiles} disabled={inflight} />

      <section className={styles.section}>
        <div className={styles.submitBar}>
          <button
            type="button"
            className={styles.submitBtn}
            disabled={!canSubmit}
            onClick={submit}
          >
            {progress.stage === "uploading" || progress.stage === "atlas"
              ? progress.label
              : "Submit project to Atlas"}
          </button>
          {!canSubmit && progress.stage === "idle" && (
            <span className={styles.submitHint}>
              {form.project_id.trim() === ""
                ? "Enter a project ID to start."
                : null}
            </span>
          )}
          {progress.stage === "atlas" && (
            <span className={styles.submitHint}>
              Atlas reads the documents and routes to Engineer or Grid. Don't
              navigate away; this can take 1-2 minutes.
            </span>
          )}
        </div>
        {errMsg && (
          <div className={styles.err}>
            Submit failed: {errMsg}
          </div>
        )}
      </section>
      </fieldset>
    </div>
  );
};

const Field = ({
  label,
  hint,
  wide,
  children,
}: {
  label: string;
  hint: string;
  wide?: boolean;
  children: React.ReactNode;
}) => (
  <div className={wide ? styles.fieldWide : styles.field}>
    <label className={styles.fieldLabel}>{label}</label>
    <div className={styles.fieldHint}>{hint}</div>
    {children}
  </div>
);
